import React, { useEffect, useRef, useState } from "react";
import { Grid, useTheme, IconButton } from "@mui/material";
import { ReactComponent as CreateIcon } from "assets/svg/image-viewer-edit.svg";
import { ReactComponent as DeleteIcon } from "assets/svg/image-viewer-delete.svg";
import { ReactComponent as EraserSVG } from "assets/svg/image-viewer-erase.svg";
import { ReactComponent as ImageIcon } from "./image_icon.svg";
import { Image, Layer, Line, Stage } from "react-konva";
import { WindowContent, WindowTitleBar } from "../../desktopWindow/components/DesktopWindow";
import { useImage } from "../hooks/useImage";
import { LineConfig, LineTool, useImageDrawing } from "../hooks/useImageDrawing";
import { ToolIconButton } from "./ToolIconButton";
import { AlertDialog, Feature } from "../../components/AlertDialog";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import { getInvertColor } from "utils/themeUtils";
import { isMobile, isDesktop } from "react-device-detect";
import { ArrowBack } from "@mui/icons-material";
import { useWindowTilesContext } from "desktopWindow/context/WindowTilesContext";
import { ActionBar } from "desktopWindow/components/DesktopWindow.styled";

interface Props {
	fileId: string;
	roomId: string;
	socket: SocketIOClient.Socket;
	imageSrc: string;
	width: number;
	height: number;
	strokeColor?: string;
	handleFileForMobile?: () => void;
	name: string | undefined;
	openFromApp?: boolean;
}

const MAX_ZOOM_PERCENTAGE = 3; // 400%
const MIN_ZOOM_PERCENTAGE = 0.1; // 20%
const ZOOM_STEP_PERCENTAGE = 0.1; // 20%

export const ImageViewer = ({
	imageSrc,
	width: fileWidth,
	height: fileHeight,
	fileId,
	roomId,
	socket,
	strokeColor = "#000",
	handleFileForMobile,
	name,
	openFromApp,
}: Props) => {
	const theme = useTheme();
	const imageEl = useImage(imageSrc);
	const containerRef = useRef<HTMLDivElement>(null);
	const [scale, setScale] = useState(isMobile ? 0.89 : 0); // Mobile view doesn't render Konva. Therefore we don't need to calculate scale.

	// Reduce the canvas width by `shrinkPecentage` to show gap between image and window border
	const canvasWidth = fileWidth * scale;
	const canvasHeight = fileHeight * scale;

	const {
		strokeWidth,
		tool,
		lines,
		changeTool,
		clearLines,
		handleMouseDown,
		handleMouseMove,
		handleMouseUp,
		currentLine,
	} = useImageDrawing({ scale, fileId, roomId, socket, strokeColor });

	const cursor = getCursor(tool, strokeWidth);
	const zoomIn = () => setScale((prev) => (prev >= MAX_ZOOM_PERCENTAGE ? prev : prev + ZOOM_STEP_PERCENTAGE));
	const zoomOut = () => setScale((prev) => (prev <= MIN_ZOOM_PERCENTAGE ? prev : prev - ZOOM_STEP_PERCENTAGE));
	const { MobileWindowResized } = useWindowTilesContext();

	useEffect(() => {
		isMobile && MobileWindowResized("FileViewer", false);
		const containerEl = containerRef.current!;
		if (scale === 0 && isDesktop) {
			// Set initial scale to fit the WindowContent size
			const fitScale = Math.min(containerEl.clientHeight / fileHeight, containerEl.clientWidth / fileWidth);
			setScale(fitScale);
		}
	}, [fileWidth, fileHeight, scale, MobileWindowResized]);

	const toolbox = (
		<Toolbox
			tool={tool}
			lines={lines}
			onChange={changeTool}
			clearLines={clearLines}
			iconColor={getInvertColor(theme.palette.primary.main)}
		>
			<Grid item>
				<ToolIconButton onClick={zoomIn}>
					<ZoomInIcon fontSize="small" style={{ fill: getInvertColor(theme.palette.primary.main) }} />
				</ToolIconButton>
			</Grid>
			<Grid item>
				<ToolIconButton onClick={zoomOut}>
					<ZoomOutIcon fontSize="small" style={{ fill: getInvertColor(theme.palette.primary.main) }} />
				</ToolIconButton>
			</Grid>
		</Toolbox>
	);

	return (
		<>
			{(isDesktop || name) && (
				<WindowTitleBar icon={<ImageIcon height="30" width="30" />}>{isDesktop && toolbox}</WindowTitleBar>
			)}
			{isMobile && (
				<ActionBar style={{ paddingLeft: isMobile ? 0 : 24 }}>
					{openFromApp && (
						<IconButton
							color="inherit"
							onClick={handleFileForMobile}
							style={{ padding: 6, position: "absolute", left: 20 }}
							size="large"
						>
							<ArrowBack style={{ color: "white" }} />
						</IconButton>
					)}
					{toolbox}
				</ActionBar>
			)}
			<WindowContent
				ref={containerRef}
				style={{
					backgroundColor: "#eee",
					...(isMobile
						? {
								display: "flex",
								flexDirection: "column",
								justifyContent: "center",
						  }
						: {}),
				}}
			>
				{/* to allow pan and pinch i had to display the pic wihthout konva */}
				{isMobile && (
					<div
						style={{
							display: "flex",
							justifyContent: "center",
						}}
					>
						<img src={imageSrc} style={{ width: window.innerWidth * scale }} alt="" />
					</div>
				)}
				{scale !== 0 && isDesktop && (
					<ImageDrawingCanvas
						cursor={cursor}
						fileWidth={fileWidth}
						fileHeight={fileHeight}
						imageEl={imageEl!}
						canvasHeight={canvasHeight}
						canvasWidth={canvasWidth}
						scale={scale}
						lines={lines}
						currentLine={currentLine}
						handleMouseDown={handleMouseDown}
						handleMouseMove={handleMouseMove}
						handleMouseUp={handleMouseUp}
					/>
				)}
			</WindowContent>
		</>
	);
};

type ImageDrawingCanvasProps = Pick<
	ReturnType<typeof useImageDrawing>,
	"handleMouseDown" | "handleMouseMove" | "handleMouseUp" | "currentLine" | "lines"
> & {
	cursor: string;
	canvasHeight: number;
	canvasWidth: number;
	scale: number;
	imageEl: HTMLImageElement;
	fileWidth: number;
	fileHeight: number;
};
export const ImageDrawingCanvas = ({
	cursor,
	canvasHeight,
	canvasWidth,
	fileHeight,
	fileWidth,
	imageEl,
	scale,
	lines,
	currentLine,
	handleMouseDown,
	handleMouseMove,
	handleMouseUp,
}: ImageDrawingCanvasProps) => {
	const renderLine = (line: LineConfig, i: number) => {
		return (
			<Line
				key={i}
				stroke={line.strokeColor}
				strokeWidth={line.strokeWidth}
				tension={0.5}
				lineCap={line.tool === "Pen" ? "round" : "square"}
				points={line.points}
				globalCompositeOperation={line.tool === "Eraser" ? "destination-out" : "source-over"}
			/>
		);
	};
	return (
		<Stage
			width={canvasWidth}
			height={canvasHeight}
			onMouseDown={handleMouseDown}
			onMouseMove={handleMouseMove}
			onMouseUp={handleMouseUp}
			style={{ cursor, display: "flex", justifyContent: "center", alignItems: "center" }}
			scale={{ x: scale, y: scale }}
		>
			<Layer>
				<Image image={imageEl} width={fileWidth} height={fileHeight} />
			</Layer>
			{isDesktop && (
				<Layer>
					{lines.map((line, i) => renderLine(line, i))}
					{currentLine && renderLine(currentLine, -1)}
				</Layer>
			)}
		</Stage>
	);
};

export const Toolbox: React.FC<{
	tool: LineTool;
	lines?: LineConfig[];
	onChange: (tool: LineTool) => void;
	clearLines: () => void;
	iconColor?: string;
}> = ({ tool, lines, onChange: changeTool, clearLines, iconColor = "#fff", children }) => {
	const [isAlertOpen, setAlertOpen] = React.useState<boolean>(false);

	const handleClear = () => {
		clearLines();
		setAlertOpen(false);
	};

	return (
		<>
			<Grid
				container
				direction="row"
				alignItems="center"
				style={{ justifyContent: "center" }}
				sx={{
					"& path": {
						fill: iconColor,
					},
				}}
			>
				{isDesktop && (
					<>
						<Grid item>
							<ToolIconButton onClick={() => changeTool("Pen")}>
								<CreateIcon fontSize="small" style={{ fill: iconColor }} />
							</ToolIconButton>
						</Grid>
						<Grid item>
							<ToolIconButton onClick={() => changeTool("Eraser")}>
								<EraserSVG fontSize="small" style={{ fill: iconColor }} />
							</ToolIconButton>
						</Grid>
						<Grid item>
							<ToolIconButton onClick={() => setAlertOpen(true)} disabled={lines?.length === 0}>
								<DeleteIcon fontSize="small" style={{ fill: iconColor }} />
							</ToolIconButton>
						</Grid>
						{/* Vertical line Separator */}
						<div
							style={{ height: 15, borderLeft: `1px solid ${iconColor}`, margin: "0 10px", opacity: 0.5 }}
						/>
					</>
				)}
				{children}
			</Grid>
			<AlertDialog
				isOpen={isAlertOpen}
				onCancel={() => setAlertOpen(false)}
				onConfirm={handleClear}
				feature={Feature.IMAGE_VIEWER}
			/>
		</>
	);
};

export function getCursor(tool: string, strokeWidth: number) {
	return tool === "Pen" ? drawingCursor(strokeWidth) : eraserCursor(strokeWidth);
}

const drawingCursor = (width: number) => {
	const widthHalf = width / 2;
	return `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" fill="%23000000" opacity="0.5" height="${width}" viewBox="0 0 ${width} ${width}" width="${width}"><circle cx="${widthHalf}" cy="${widthHalf}" r="${widthHalf}" fill="%23000000" /></svg>') ${widthHalf} ${widthHalf}, auto`;
	// return `url(data:image/svg+xml;utf8,${Pencil}), auto`;
};
const eraserCursor = (width: number) => {
	const widthHalf = width / 2;
	return `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" fill="%23000000" opacity="0.5" height="${width}" viewBox="0 0 ${width} ${width}" width="${width}"><rect width="${width}" height="${width}" fill="%23000000" /></svg>') ${widthHalf} ${widthHalf}, auto`;
};
